/** First authored frontier kit. Geometry, collision and activity slots are one
 * deterministic place plan. Metres, local +Y up, fronts face +Z. */
import type { AssetNode, AssetVec3, ProceduralAssetV1 } from '../proceduralAssets.js';
import { rngFromString } from '../rng.js';
import {workshopDetails,frontierStorehouse,type WorkplacePlan} from './workplace.js';
import {frontierMarket,frontierGarden} from './marketGarden.js';
import { frontierHome } from './frontierHome.js';
import { frontierExtra } from './frontierExtras.js';
import type {MerchantCottage} from './frontierHome.js';
import * as pmath from '../pmath.js';
import {civicBuilding} from './civic.js';
import type {CivicInterior} from './civicHall.js';
export type FrontierPlaceKind = 'home' | 'workshop' | 'market' | 'monument' | 'chapel' | 'watchtower' | 'storehouse' | 'well' | 'farm' | 'admin';
export interface PlaceBox {
    center: AssetVec3;
    size: AssetVec3;
}
export interface ActivitySlot {
    id: string;
    activity: 'garden' | 'repair' | 'serve' | 'browse' | 'inspect';
    position: AssetVec3;
    approach: AssetVec3;
    heading: number;
    capacity: 1;
}
export interface CanopyPlan {
    id: string;
    x: number;
    width: number;
    backZ: number;
    frontZ: number;
    backY: number;
    frontY: number;
    sag: number;
    ridge?: number;
    material: number;
    attachment: 'wall' | 'frame';
}
/** A single continuous cloth surface. Boundary values are shared with beams. */
export function canopyPoint(c: CanopyPlan, u: number, v: number): AssetVec3 {
    const ridge=(c.ridge??0)*(1-Math.abs(2*v-1));
    const sag=c.sag*16*u*(1-u)*v*(1-v)*(c.ridge?4*Math.abs(v-.5):1);
    return [c.x + (u - .5) * c.width,
        c.backY + (c.frontY - c.backY) * v + ridge - sag,
        c.backZ + (c.frontZ - c.backZ) * v];
}
export interface FrontierPlacePlan {
    version: 1;
    kind: FrontierPlaceKind;
    asset: ProceduralAssetV1;
    /** Bounded sub-assets for a compound building; collision remains one plan. */
    assetParts?:ProceduralAssetV1[];
    obstacles: PlaceBox[];
    floors: PlaceBox[];
    /** Thin shared roof strips: bounded approximation of the tiled deck. */
    roofs?: PlaceBox[];
    entrance: AssetVec3;
    entryApproach: AssetVec3;
    slots: ActivitySlot[];
    canopies: CanopyPlan[];
    cottage?:MerchantCottage;
    workplace?:WorkplacePlan;
    civicInterior?:CivicInterior;
    design: {
        homeForm: 'wing'|'cross-gable'|'veranda';
        workshopForm: 'forge'|'cross-bay'|'long-bay';
        storehouseForm: 'loft'|'loading-wing';
        roofPitch: number;
        homeWidth: number;
        porchWidth: number;
        marketBays: number;
        marketHeight: number;
        crystalHeight: number;
        spires: number;
        mirrored: boolean;
    };
}
export interface TownPlacement {
    id: string;
    plan: FrontierPlacePlan;
    position: AssetVec3;
    quarterTurn: number;
}
export interface FrontierTownPlan {
    seed: string;
    places: TownPlacement[];
    promenade: AssetVec3[];
}
const materials = [
    { color: 0xeee6d4, roughness: .95, metalness: 0 }, // plaster
    { color: 0x62452b, roughness: .9, metalness: 0 }, // timber
    { color: 0xc45b3c, roughness: .9, metalness: 0 }, // tile
    { color: 0xd47a50, roughness: .9, metalness: 0 }, // tile light
    { color: 0xc0b499, roughness: 1, metalness: 0 }, // stone
    { color: 0x275b88, roughness: .7, metalness: 0 }, // blue accents
    { color: 0xf1d9a0, roughness: 1, metalness: 0 }, // linen
    { color: 0x364237, roughness: .7, metalness: .35 }, // iron
    { color: 0x4b7130, roughness: 1, metalness: 0 }, // planting
    { color: 0xe6a63e, roughness: .8, metalness: 0 }, // produce
    { color: 0x51b9f4, roughness: .25, metalness: .1, emissive: 0x1a81da, emissiveIntensity: 1.2 },
    { color: 0xff922d, roughness: .7, metalness: 0, emissive: 0xff650c, emissiveIntensity: 1.8 },
    { color: 0x92734e, roughness: 1, metalness: 0 }, // worn interior timber
    { color: 0x466969, roughness: .25, metalness: .1 }, // recessed quench water
    { color: 0x493829, roughness: 1, metalness: 0 }, // cultivated soil / vessel interior
    { color: 0x83a34b, roughness: 1, metalness: 0 }, // young leaves
];
export function frontierPlace(kind: FrontierPlaceKind, seed: string): FrontierPlacePlan {
    if(kind==='admin'||kind==='monument')return civicBuilding(kind,seed,2);
    // Independent streams: a new detail never rerolls the structural choices.
    const form = rngFromString(`${seed}:${kind}:form:v2`);
    const design = { roofPitch: [.48, .58, .66][Math.floor(form() * 3)],
        homeWidth: [7.6, 8.2, 9][Math.floor(form() * 3)], porchWidth: [3.2, 3.8][Math.floor(form() * 2)],
        marketBays: form() < .5 ? 2 : 3, marketHeight: [3.2, 3.5, 3.8][Math.floor(form() * 3)], crystalHeight: [3, 3.6, 4.2][Math.floor(form() * 3)], spires: form() < .5 ? 4 : 6, mirrored: form() < .5 };
    const composition = rngFromString(`${seed}:${kind}:composition:v1`);
    const composedDesign = {...design, homeForm:(['wing','cross-gable','veranda'] as const)[Math.floor(composition()*3)], workshopForm:(['forge','cross-bay','long-bay'] as const)[Math.floor(composition()*3)], storehouseForm:(['loft','loading-wing'] as const)[Math.floor(composition()*2)]};
    const canopies: CanopyPlan[] = [], roofs: PlaceBox[] = [];
    let entrance:AssetVec3=[0,0,6],cottage:MerchantCottage|undefined,workplace:WorkplacePlan|undefined;
    const rng = rngFromString(`${seed}:${kind}:detail:v2`), nodes: AssetNode[] = [], obstacles: PlaceBox[] = [], floors: PlaceBox[] = [], slots: ActivitySlot[] = [];
    let componentId='';const componentCounts=new Map<string,number>();
    const part=(id:string,build:()=>void)=>{const previous=componentId;componentId=id;try{build();}finally{componentId=previous;}};
    const node = (kind: AssetNode['kind'], params: number[], p: AssetVec3, material: number, r: AssetVec3 = [0, 0, 0]) => {
        const count=componentCounts.get(componentId)??0;componentCounts.set(componentId,count+1);
        const n: AssetNode = { id: componentId?`${componentId}:${count}`:`n${nodes.length}`, kind, params, p, r, material };
        nodes.push(n);
        return n;
    };
    const box = (size: AssetVec3, p: AssetVec3, mat: number, solid = false, r: AssetVec3 = [0, 0, 0]) => {
        node('box', size, p, mat, r);
        if (solid) {
            const c=Math.abs(pmath.cos(r[1])),s=Math.abs(pmath.sin(r[1]));
            obstacles.push({ center: p, size: r[1]===0?size:[size[0]*c+size[2]*s,size[1],size[0]*s+size[2]*c] });
        }
    };
    const floor = (w: number, d: number, x = 0, z = 0, h = .3) => { box([w, h, d], [x, h / 2, z], 4); floors.push({ center: [x, h / 2, z], size: [w, h, d] }); };
    const post = (x: number, z: number, h = 3.4, y = .3) => box([.2, h, .2], [x, y + h / 2, z], 1, true);
    const barrel = (x: number, z: number, base = .3) => {
        node('cylinder', [.37, .34, .85], [x, base + .425, z], 1);
        for (const y of [base + .09, base + .74])
            node('cylinder', [.38, .38, .045], [x, y, z], 7);
        obstacles.push({ center: [x, base + .425, z], size: [.76, .85, .76] });
    };
    const crate = (x: number, z: number) => {
        box([.85, .75, .75], [x, .675, z], 1, true);
        for (const yy of [.36, .98])
            box([.89, .07, .79], [x, yy, z], 3);
    };
    const roof = (w: number, d: number, eave: number, originX=0, originZ=0, pitch=design.roofPitch, quarterTurn=0) => {
        const first=nodes.length;
        const angle = pitch, slope = pmath.sin(angle) / pmath.cos(angle), half = d / 2, peak = eave + half * slope;
        const strips=Math.ceil(d/.3),step=d/strips;
        for(let i=0;i<strips;i++){
            const z=-half+(i+.5)*step,y=peak-Math.abs(z)*slope+.16;
            roofs.push({center:[originX+(quarterTurn?z:0),y,originZ+(quarterTurn?0:z)],size:quarterTurn?[step,.24,w]:[w,.24,step]});
        }
        for (const side of [-1, 1]) {
            box([w, .18, half / pmath.cos(angle)], [0, (eave + peak) / 2, side * half / 2], 1, false, [side * angle, 0, 0]);
            for (let row = 0; row < 6; row++) {
                const z = (row + .5) * half / 6;
                for(let lane=0;lane<3;lane++){
                    const tile = node('box', [w / 12 - .025, .11, half / 6 / pmath.cos(angle) + .1], [-w / 2 + (lane+.5)*w/12, peak - z * slope + .13 + (5-row)*.014, side * z], rng() > .45 ? 2 : 3, [side * angle, 0, 0]);
                    tile.modifiers = [{ type: 'array', mode: 'linear', count: 4, offset: [w / 4, 0, 0] }];
                }
            }
            box([w + .15, .19, .19], [0, eave, side * half], 1);
        }
        box([w + .15, .24, .23], [0, peak + .22, 0], 2);
        for(const n of nodes.slice(first)){
            const x=n.p[0],z=n.p[2];n.p[0]=originX+(quarterTurn?z:x);n.p[2]=originZ+(quarterTurn?-x:z);
            if(quarterTurn){
                // Boxes are symmetric: exchange their horizontal axes and
                // express pitch about Z, avoiding an incorrect XYZ Euler yaw.
                const rx=n.r[0],width=n.params![0];n.params![0]=n.params![2];n.params![2]=width;n.r=[0,0,-rx];
            }
            for(const m of n.modifiers??[])if(quarterTurn&&m.type==='array'&&m.mode==='linear'){const dx=m.offset[0];m.offset[0]=m.offset[2];m.offset[2]=-dx;}
        }
    };
    const frameEdges = new Set<string>();
    const awning = (id: string, x: number, w: number, backZ: number, frontZ: number, backY: number, frontY: number, attachment: CanopyPlan['attachment'],ridge=0,material=6) => {
        const c: CanopyPlan = { id, x, width: w, backZ, frontZ, backY, frontY, sag: .16, material, attachment,...(ridge?{ridge}:{}) };
        canopies.push(c);
        // Front posts embed in the beam. Rear posts are required
        // for freestanding stalls; attached awnings share the building's ledger.
        for (const xx of [x - w / 2, x + w / 2]) {
            const key = [xx, backZ, frontZ, backY, frontY,ridge].map(v => v.toFixed(5)).join(':');
            if (frameEdges.has(key))
                continue;
            frameEdges.add(key);
            post(xx, frontZ, frontY - .37);
            if (attachment === 'frame')
                post(xx, backZ, backY - .37);
            // Side rails slope between the same endpoints as the cloth.
            const sections=ridge?[[backZ,backY],[(backZ+frontZ)/2,(backY+frontY)/2+ridge],[frontZ,frontY]]:[[backZ,backY],[frontZ,frontY]];
            for(let i=1;i<sections.length;i++){
                const [az,ay]=sections[i-1],[bz,by]=sections[i],dz=bz-az,dy=by-ay;
                box([.12,.12,Math.sqrt(dz*dz+dy*dy)],[xx,(ay+by)/2-.06,(az+bz)/2],1,false,[-pmath.atan2(dy,dz),0,0]);
            }
        }
        box([w + .18, .14, .16], [x, frontY - .07, frontZ], 1);
        box([w + .18, .14, .16], [x, backY - .07, backZ], 1);
        if(ridge)box([w+.18,.14,.16],[x,(backY+frontY)/2+ridge-.07,(backZ+frontZ)/2],1);
    };
    const slot = (id: string, activity: ActivitySlot['activity'], p: AssetVec3, approach: AssetVec3, heading = 0) => {
        slots.push({ id, activity, position: p, approach, heading, capacity: 1 });
        nodes.push({ id: `n${nodes.length}`, name: `ground:activity:${id}`, kind: 'socket', p, r: [0, heading, 0] });
    };
    if (kind === 'home') {
        cottage=frontierHome({node,box,floor,post,roof,barrel,crate,slot,awning,part},composedDesign);
        entrance=cottage.entrance;
    }
    else if (kind === 'workshop') {
        floor(9, 7);
        workplace=workshopDetails({node,box,floor,post,roof,barrel,crate,slot,awning,part},composedDesign);
        for (const x of [-4, 0, 4])
            post(x, 3, composedDesign.workshopForm!=='long-bay'&&x<=0?4.5:3.8);
        post(4, -3, 3.8);
        box([8.4, .3, .3], [0, 4, 3], 1);
        for (const x of [-3.5, 3.5])
            box([1.5, .18, .18], [x, 3.55, 3], 1, false, [0, 0, x > 0 ? -.65 : .65]);
        const divided=composedDesign.workshopForm!=='long-bay',forgeEave=divided?4.9:4.15;
        if(divided){
            box([4.15,.8,.3],[-2,4.4,-3],0,true);
            box([.3,.8,6],[-4,4.4,0],0,true);
            box([.22,.8,6],[0,4.4,0],0,true);
            box([4.25,.26,.28],[-2,4.75,3],1);
            roof(4.5,7.5,forgeEave,-2,0);
            if(composedDesign.workshopForm==='cross-bay')roof(7.5,4.5,4.15,2,0,.4,1);
            else roof(4.5,7.5,4.15,2,0,.4);
        }else roof(9,7.5,forgeEave);

        // Close the masonry end at the actual roof underside, and frame the
        // open end as a timber truss. Both derive from the forge bearing.
        const forgeSlope=pmath.sin(design.roofPitch)/pmath.cos(design.roofPitch);
        const wallTop=divided?4.8:4,base=forgeEave+.75*forgeSlope-.09/pmath.cos(design.roofPitch),rise=3*forgeSlope;
        box([.3,base-wallTop,6],[-4,(base+wallTop)/2,0],0);
        const infill=node('box',[.3,rise,6],[-4,base+rise/2,0],0);infill.modifiers=[{type:'taper',axis:'y',factor:0}];
        for(const z of [-2.85,2.85])box([.22,base-.3,.22],[-4.17,(base+.3)/2,z],1);
        box([.24,.2,6.2],[-4.17,base,0],1);
        for(const side of [-1,1])box([.24,.2,3/pmath.cos(design.roofPitch)],[-4.17,base+rise/2,side*1.5],1,false,[side*design.roofPitch,0,0]);
        box([.25,rise,.2],[-4.17,base+rise/2,0],1);
        // Small loft vent gives the tall gable a readable purpose.
        box([.05,.65,.6],[-4.18,base+.48,0],7);
        for(const z of [-.2,0,.2])box([.08,.65,.045],[-4.22,base+.48,z],1);
        const openX=divided?0:4;
        for(const z of [-3,3])box([.22,base-(divided?4.75:4),.22],[openX,(base+(divided?4.75:4))/2,z],1);
        box([.22,.2,6.2],[openX,base,0],1);
        box([.22,rise,.2],[openX,base+rise/2,0],1);
        for(const side of [-1,1])box([.22,.2,3/pmath.cos(design.roofPitch)],[openX,base+rise/2,side*1.5],1,false,[side*design.roofPitch,0,0]);

        box([2, 1.2, 1.1], [-2.5, .9, -2], 4, true);
        box([1.2, .48, .12], [-2.5, 1.32, -1.4], 11);
        box([1.9, .25, 1.25], [-2.5, 2.2, -2], 7);
        const chimneyTop=forgeEave+(3.75-2.1)*pmath.sin(design.roofPitch)/pmath.cos(design.roofPitch)+1.2;
        box([.8,chimneyTop-2.1,.8],[-2.5,(chimneyTop+2.1)/2,-2.1],4);
        box([1,.2,1],[-2.5,chimneyTop,-2.1],4);
        box([2.2, .18, .9], [2, 1.35, -1], 1, true);
        for (const x of [1.1, 2.9])
            post(x, -1, 1, .3);
        box([.7, .6, .55], [-.8, .6, .5], 1, true);
        box([1.1, .25, .5], [-.8, 1.03, .5], 7);
        crate(3.1, 2);
        barrel(-3.2, 4.05, 0);
        floor(4.4, 2.55, 2, 4.1);
        awning('work-bay', 2, 4, 3.04, 5.14, 3.5, 3.185, 'wall');
        entrance=workplace.entrance;
        slot('bench', 'repair', [2, .3, .15], [2, 0, 5.8], Math.PI);
    }
    else if(kind==='market'||kind==='farm'){
        workplace=(kind==='market'?frontierMarket:frontierGarden)({node,box,floor,post,roof,barrel,crate,slot,awning,part},composedDesign,seed);
        entrance=workplace.entrance;
    }
    else if(kind==='storehouse'){workplace=frontierStorehouse({node,box,floor,post,roof,barrel,crate,slot,awning,part},composedDesign);entrance=workplace.entrance;}
    else entrance=frontierExtra(kind,{node,box,floor,post,roof,barrel,crate,slot,awning,part},composedDesign);
    if (design.mirrored) {
        entrance[0]*=-1;
        for (const n of nodes) {
            n.p[0] *= -1;
            n.r[1] *= -1;
            n.r[2] *= -1;
            for (const m of n.modifiers ?? [])
                if (m.type === 'array' && m.mode === 'linear')
                    m.offset[0] *= -1;
        }
        // Box centres share node positions: they have already been mirrored above.
        // Floors/obstacles are re-derived separately below when their centres don't alias.
        const positions = new Set(nodes.map(n => n.p));
        for (const b of [...obstacles, ...floors, ...roofs])
            if (!positions.has(b.center))
                b.center[0] *= -1;
        for (const slot of slots) {
            if (!positions.has(slot.position))
                slot.position[0] *= -1;
            slot.approach[0] *= -1;
            slot.heading *= -1;
        }
        for (const c of canopies)
            c.x *= -1;
    }
    if(cottage&&design.mirrored){
        // Metadata uses independent vectors; mirror once, as with geometry.
        for(const f of cottage.facades){f.center[0]*=-1;if(f.axis==='x')for(const o of f.openings)o.u*=-1;else f.outward=f.outward===1?-1:1;}
        cottage.stockFacing[0]*=-1;
        for(const p of [...cottage.restockRoute,...cottage.destinations.map(d=>d.position)])p[0]*=-1;
    }
    if(workplace&&design.mirrored){
        for(const f of workplace.facades){f.center[0]*=-1;if(f.axis==='x')for(const o of f.openings)o.u*=-1;else f.outward=f.outward===1?-1:1;}
        for(const plot of workplace.plots??[]){plot.center[0]*=-1;plot.workPosition[0]*=-1;}
        for(const p of [...workplace.route,...workplace.destinations.map(d=>d.position),workplace.firstFacing,workplace.lastFacing])p[0]*=-1;
    }
    const entryApproach:AssetVec3=[entrance[0],0,entrance[2]+3];
    nodes.push({ id: `n${nodes.length}`, name: 'ground:entrance', kind: 'socket', p: entrance, r: [0, 0, 0] });
    return { version: 1, kind, asset: { version: 1, id: `frontier-${kind}`, name: `Frontier ${kind}`, kind: 'buildingModule', tags: ['frontier', 'town-v2'], buildingModule: { footprintUnits: [3, 3] }, materials: materials.map(m => ({ ...m })), nodes }, obstacles, floors, roofs, entrance, entryApproach, slots, canopies, ...(cottage?{cottage}:{}), ...(workplace?{workplace}:{}), design:composedDesign };
}
/** Stable quarter-turn transform shared by rendering, activity and collision. */
export function placePoint(p: AssetVec3, placement: Pick<TownPlacement, 'position' | 'quarterTurn'>): AssetVec3 {
    const q = ((placement.quarterTurn % 4) + 4) % 4, x = [p[0], p[2], -p[0], -p[2]][q], z = [p[2], -p[0], -p[2], p[0]][q];
    return [x + placement.position[0], p[1] + placement.position[1], z + placement.position[2]];
}
export function frontierTown(seed = 'first-light'): FrontierTownPlan {
    return { seed, places: [
            { id: 'home', plan: frontierPlace('home', seed), position: [-13, 0, -5], quarterTurn: 1 },
            { id: 'workshop', plan: frontierPlace('workshop', seed), position: [13, 0, -5], quarterTurn: 3 },
            { id: 'market', plan: frontierPlace('market', seed), position: [0, 0, -15], quarterTurn: 0 },
            { id: 'monument', plan: frontierPlace('monument', seed), position: [0, 0, 0], quarterTurn: 0 },
            { id: 'chapel', plan: frontierPlace('chapel', seed), position: [0, 0, -47], quarterTurn: 0 },
            { id: 'storehouse', plan: frontierPlace('storehouse', seed), position: [-27, 0, -26], quarterTurn: 1 },
            { id: 'watchtower', plan: frontierPlace('watchtower', seed), position: [26, 0, -29], quarterTurn: 3 },
            { id: 'well', plan: frontierPlace('well', seed), position: [-10.8, 0, 4.8], quarterTurn: 1 },
            { id: 'farm', plan: frontierPlace('farm', seed), position: [-39, 0, 0], quarterTurn: 1 },
            { id: 'admin', plan: frontierPlace('admin', seed), position: [25, 0, -48], quarterTurn: 0 },
        ], promenade: [[-5, 0, 7], [-6, 0, -7], [6, 0, -7], [6, 0, 7], [-5, 0, 7]] };
}
/** Static upright body check for the fixture; roofs remain visual geometry. */
export function townWalkable(town: FrontierTownPlan, p: AssetVec3, radius = .3): boolean {
    for (const place of town.places)
        for (const b of place.plan.obstacles) {
            const c = placePoint(b.center, place), size = place.quarterTurn % 2 ? [b.size[2], b.size[1], b.size[0]] : b.size;
            if (c[1] + size[1] / 2 <= p[1] + .05 || c[1] - size[1] / 2 >= p[1] + 1.75)
                continue;
            if (Math.abs(p[0] - c[0]) < size[0] / 2 + radius && Math.abs(p[2] - c[2]) < size[2] / 2 + radius)
                return false;
        }
    return true;
}
/** Stable reservations: independent of avatar visibility and input order. */
export function reserveTownActivities(town: FrontierTownPlan, requests: {
    resident: string;
    activity: ActivitySlot['activity'];
    place?:string;
}[]) {
    const free = town.places.flatMap(p => p.plan.slots.map(s => ({ place: p, slot: s, key: `${p.id}:${s.id}` })));
    const assigned = new Map<string, typeof free[number]>();
    for (const r of [...requests].sort((a, b) => a.resident < b.resident ? -1 : a.resident > b.resident ? 1 : 0)) {
        if (assigned.has(r.resident))
            continue;
        const i = free.findIndex(s => s.slot.activity === r.activity&&(!r.place||s.place.id===r.place));
        if (i >= 0)
            assigned.set(r.resident, free.splice(i, 1)[0]);
    }
    return assigned;
}
